import {
  ActionEmptyState,
  ActionConfirmationModal,
  Button,
  Skeleton,
  Text,
  Typography,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useDeleteMatterTag } from "@/lib/data/react-query";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { getMatterTagChipClassNames } from "@/lib/utils/matters/matter-tag-color";
import { cn } from "@/lib/utils/cn";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";
import { sortBy } from "lodash";
import { useMemo, useState } from "react";
import { AddOrEditMatterTagModal } from "./AddOrEditMatterTagModal";
import { MatterTagContextMenu } from "./MatterTagContextMenu";

function MatterTagsViewDangerous() {
  const { hasActionPermission } = useUser();
  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const [isAddingTag, setIsAddingTag] = useState(false);
  const [activeMatterTagId, setActiveMatterTagId] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [matterTagToDeleteId, setMatterTagToDeleteId] = useState("");
  const deleteMatterTag = useDeleteMatterTag();
  const canCreateMatterTags = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTag,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdateMatterTags = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTag,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteMatterTags = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTag,
    ACTION_PERMISSION_VALUES.delete,
  );

  const matterTags = useMemo(
    () =>
      sortBy(matterSettingsData?.data.tags ?? [], (tag) =>
        tag.label.toLowerCase(),
      ),
    [matterSettingsData?.data.tags],
  );

  const activeMatterTag = matterTags.find(
    (tag) => tag.id === activeMatterTagId,
  );
  const matterTagToDelete = matterTags.find(
    (tag) => tag.id === matterTagToDeleteId,
  );

  const handleDeleteMatterTag = async () => {
    if (!matterTagToDelete) return;

    await deleteMatterTag.mutateAsync({ id: matterTagToDelete.id });
  };

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="gap-y-1">
          <Text variant="title">Tags</Text>
        </div>
        {canCreateMatterTags ? (
          <Button
            className="shrink-0 self-start"
            onClick={() => {
              setIsAddingTag(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add tag
          </Button>
        ) : null}
      </div>

      <div>
        {isMatterSettingsLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-[88px] rounded-2xl" />
            ))}
          </div>
        ) : isMatterSettingsError ? (
          <Typography variant="subHeading">
            {serverErrorFallbackText}
          </Typography>
        ) : matterTags.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {matterTags.map((tag) => (
              <MatterTagContextMenu
                key={tag.id}
                tag={tag}
                canEdit={canUpdateMatterTags}
                canDelete={canDeleteMatterTags}
                onEdit={() => {
                  setActiveMatterTagId(tag.id);
                  setIsEditing(true);
                }}
                onDelete={() => {
                  setMatterTagToDeleteId(tag.id);
                }}
                trigger={
                  <button
                    type="button"
                    className={cn(
                      "group relative flex min-h-[88px] cursor-pointer items-center rounded-2xl border px-5 py-4 text-left text-base font-medium transition-all",
                      "focus-visible:ring-ring hover:bg-gray-100 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
                      getMatterTagChipClassNames(tag.color),
                    )}
                    onClick={() => {
                      setActiveMatterTagId(tag.id);
                      setIsEditing(true);
                    }}
                  >
                    <span className="truncate">{tag.label}</span>
                    {canUpdateMatterTags ? (
                      <span
                        className={cn(
                          "text-muted-foreground ml-auto inline-flex h-9 w-9 shrink-0 items-center justify-center opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-visible:opacity-100",
                        )}
                      >
                        <span className="sr-only">Edit {tag.label}</span>
                        <Pencil className="h-4 w-4" aria-hidden />
                      </span>
                    ) : null}
                  </button>
                }
              />
            ))}
          </div>
        ) : (
          <ActionEmptyState
            title="No matter tags yet"
            description="Create tags to organize matters and make them easier to filter later."
            actions={
              canCreateMatterTags ? (
                <Button
                  onClick={() => {
                    setIsAddingTag(true);
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Add tag
                </Button>
              ) : null
            }
          />
        )}
      </div>

      {activeMatterTag ? (
        <AddOrEditMatterTagModal
          editing={true}
          activeMatterTag={activeMatterTag}
          setOpen={setIsEditing}
          setActiveMatterTagId={setActiveMatterTagId}
          open={isEditing}
          isReadOnly={!canUpdateMatterTags}
        />
      ) : null}

      <AddOrEditMatterTagModal
        editing={false}
        open={isAddingTag}
        setOpen={setIsAddingTag}
        setActiveMatterTagId={setActiveMatterTagId}
      />

      <ActionConfirmationModal
        open={Boolean(matterTagToDelete)}
        setOpen={(open) => {
          if (!open) setMatterTagToDeleteId("");
        }}
        title="Delete matter tag"
        description={`Are you sure you want to delete "${matterTagToDelete?.label}"? This action cannot be undone.`}
        afterActionFn={handleDeleteMatterTag}
      />
    </div>
  );
}

export default function MatterTagsView() {
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      }
    >
      <MatterTagsViewDangerous />
    </ErrorBoundary>
  );
}
